import React, { useState } from 'react';
import { Card, CardContent, CardActions, Typography, Chip, Box, IconButton, Tooltip, Checkbox } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import QualityScore from './QualityScore';
import { format } from 'date-fns';
import useDmsPalette from '../hooks/useDmsPalette';
import { dmsApi } from '../services/dmsApi';
import FileTypeIcon from './FileTypeIcon';
import ClassificationBadge from './ClassificationBadge';

const STATUS_COLORS = {
  draft: 'default', review: 'warning', published: 'success', archived: 'error',
};

export default function DocumentCard({ doc, onEdit, onDelete, onSelect, selectable = false, selected = false }) {
  const MP = useDmsPalette();
  const [isFavorite, setIsFavorite] = useState(() => {
    const favs = JSON.parse(localStorage.getItem('dms-favorites') || '[]');
    return favs.includes(doc._id);
  });

  const toggleFavorite = (e) => {
    e.stopPropagation();
    const favs = JSON.parse(localStorage.getItem('dms-favorites') || '[]');
    const newFavs = isFavorite ? favs.filter(id => id !== doc._id) : [...favs, doc._id];
    localStorage.setItem('dms-favorites', JSON.stringify(newFavs));
    setIsFavorite(!isFavorite);
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    window.open(dmsApi.downloadDocument(doc._id), '_blank');
  };

  return (
    <Card
      sx={{ position: 'relative', cursor: 'pointer', '&:hover': { borderColor: MP.blue, transform: 'translateY(-1px)', transition: 'all 0.15s' } }}
      onClick={() => selectable ? onSelect(!selected) : onSelect(doc)}
    >
      {selectable && (
        <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 1 }}>
          <Checkbox
            checked={selected}
            onChange={(e) => { e.stopPropagation(); onSelect(e.target.checked); }}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
        </Box>
      )}
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          <FileTypeIcon mimeType={doc.mimeType} sx={{ mt: 0.3, flexShrink: 0 }} />
          <Typography variant="subtitle2" fontWeight={600} noWrap title={doc.title}>
            {doc.title}
          </Typography>
        </Box>
        {doc.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {doc.description}
          </Typography>
        )}
        {doc.aiSummary && (
          <Typography variant="caption" sx={{ color: MP.green, display: 'block', mb: 1 }}>
            🤖 {doc.aiSummary.substring(0, 100)}...
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          <Chip label={doc.status} size="small" color={STATUS_COLORS[doc.status] || 'default'} />
          <ClassificationBadge classification={doc.classification} />
          {doc.tags?.slice(0, 3).map(tag => (
            <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 18 }} />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          v{doc.version} · {doc.createdBy?.name || 'Unknown'} · {format(new Date(doc.updatedAt), 'MMM d, yyyy')}
        </Typography>
      </CardContent>
      <CardActions sx={{ pt: 0, px: 2, pb: 1 }}>
        <Tooltip title="Download"><IconButton size="small" onClick={handleDownload}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Edit"><IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(doc); }}><EditIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title="Delete"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); onDelete(doc); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
        <Tooltip title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}>
          <IconButton size="small" onClick={toggleFavorite} sx={{ color: isFavorite ? 'warning.main' : 'text.disabled' }}>
            {isFavorite ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <QualityScore doc={doc} size="small" />
      </CardActions>
    </Card>
  );
}
